import { useCallback, useEffect, useRef } from "react";

type ResizeObserverRefCallback<E extends Element> = (element: E | null) => void;

/**
 * Usage
 * ```tsx
 * const MyComponent = () => {
 *     const elementRef = useResizeObserver((entry) => { ... do something ...})
 *
 *     return <div ref={elementRef} />
 * }
 * ```
 * @param callback Callback gets fired everytime ResizeObserver entry call is fired.
 */
export const useResizeObserver = <E extends Element>(
  callback: (entry: ResizeObserverEntry, observer: ResizeObserver) => void
): ResizeObserverRefCallback<E> => {
  const observerRef = useRef<ResizeObserver | null>(null);

  // Always use the latest callback, without having to need to run useEffect again
  const callBackRef = useRef(callback);
  // oxlint-disable-next-line react/react-compiler -- FIXME(CLI-289): auto-suppressed during shared-config migration.
  callBackRef.current = callback;

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  return useCallback((e: E | null) => {
    if (!e) {
      return;
    }

    const observer = new ResizeObserver((entries, observer) => {
      entries.forEach((entry) => {
        callBackRef.current(entry, observer);
      });
    });
    observerRef.current?.disconnect();
    observer.observe(e);
    observerRef.current = observer;
  }, []);
};
